// tradeOrderText — the JAC sales order in Books, as the trade card shows it (Oct 29, 2026). Pure: no Supabase
// client in here, so the unit tests import it directly; src/lib/trades.ts re-exports it with fetchTradeOrders.
//
// Greg, Oct 29: "lets send a sales order to books for the trade to confirm the invoice matches the sales order
// pricing and measurements before paying the invoice" / "allow shannon to adjust the sales order as needed and a
// notification sent to chad for the discrepancy". And: "no need to show the amount bardin pays jac. the reps only
// care about the amount that we need to charge homeowner and where their commission starts." So the chip says where
// the order stands in words and never shows a sub cost amount: Books' difference sentences that carry dollars stay
// off the card (a footage one, "Billed 210 ft; the order is 199 ft.", can show).
//
// The order is one per job (the price row): key 'trade_jobs:' + (pair_id ?? the trade's id), the same on both
// visits of a detach and its reset. Migration 20261029h keeps it in trade_books_orders (price viewers only).

export type TradeOrderStatus = "not_sent" | "open" | "billed" | "differs" | "adjusted" | "paid" | "voided";

/** A trade_books_orders row as the card reads it: no money columns are selected. */
export interface TradeOrder {
  job_key: string;
  books_status: TradeOrderStatus;
  sent_at: string | null;
  confirmed_at: string | null;
  attempts: number;
  last_error: string | null;
  voided: boolean;
  invoice_number: string | null;
  invoice_date: string | null;
  /** Books' sentences when JAC's bill differs; the ones with a dollar amount never reach the card. */
  match_differences: string[] | null;
  adjusted_reason: string | null;
  adjusted_by_name: string | null;
  adjusted_at: string | null;
  paid_on: string | null;
  updated_at: string;
}

/** The columns fetchTradeOrders selects (billed_total, order_total, paid_total, adjusted_from_total and bills stay in the table). */
export const TRADE_ORDER_COLS =
  "job_key,books_status,sent_at,confirmed_at,attempts,last_error,voided,invoice_number,invoice_date,match_differences,adjusted_reason,adjusted_by_name,adjusted_at,paid_on,updated_at";

/** The job's order key: the pair's id for a detach and its reset, else the trade's own. */
export function tradeJobKey(t: { id: string; pair_id: string | null }): string {
  return `trade_jobs:${t.pair_id ?? t.id}`;
}

export type TradeOrderTone = "slate" | "sky" | "emerald" | "amber" | "rose";

export interface TradeOrderChipView {
  label: string;
  tone: TradeOrderTone;
  /** The hover text: more words, still no sub cost. */
  title?: string;
}

/** "JAC" for JAC Gutters; the sub's name otherwise. */
export function subShortName(vendor: string | null | undefined): string {
  const name = (vendor ?? "").trim();
  if (!name) return "The sub";
  return /^jac\b/i.test(name) ? "JAC" : name;
}

/** "Shannon" from "Shannon Fowler"; null for nothing. */
export function firstName(name: string | null | undefined): string | null {
  const n = (name ?? "").trim();
  if (!n) return null;
  if (n.includes("@")) return n.split("@")[0];
  return n.split(/\s+/)[0];
}

/** Free text from Books (Shannon's reason for an adjustment) as the card may show it: every dollar figure masked
 *  (Greg: no sub cost on the card; "JAC raised gutters to $7.00" reads "JAC raised gutters to …"). Chad's
 *  notification keeps the reason whole. */
export function withoutMoney(text: string | null | undefined): string {
  return (text ?? "").replace(/-?\$\s?\d[\d,]*(\.\d+)?/g, "…");
}

/** Where the trade card shows the job's order chip (review, Oct 29): in the price block whenever the card shows a
 *  price, the visit's own or its partner's (the order is the job's, one key for both visits of a pair); on its own
 *  for an open visit of a JAC pair whose price isn't on screen (the done partner that holds it has left the Trades
 *  view's 14 days). Anyone kept off prices reads no order (RLS), so the chip shows them nothing. */
export function orderChipPlace(
  t: { pair_id: string | null; vendor_id: string | null; status: string },
  showsPrice: boolean,
): "price" | "alone" | null {
  if (showsPrice) return "price";
  const open = t.status === "pending" || t.status === "scheduled" || t.status === "in_progress";
  return open && !!t.pair_id && !!t.vendor_id ? "alone" : null;
}

/** Books' difference sentences the card may show: none that names a dollar amount (Greg: no sub cost on the card). */
export function differencesWithoutMoney(diffs: string[] | null | undefined): string[] {
  return (Array.isArray(diffs) ? diffs : []).filter((s) => typeof s === "string" && s.trim() && !s.includes("$"));
}

/** "Nov 5" for a date-only string, without a timezone slip. */
function fmtDay(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** What the trade card's order chip says, or null when there is nothing to show (no order, not sent, withdrawn). */
export function tradeOrderChip(o: TradeOrder | null | undefined, vendor?: string | null): TradeOrderChipView | null {
  if (!o) return null;
  const sub = subShortName(vendor);
  const inv = o.invoice_number ? ` ${o.invoice_number}` : "";
  switch (o.books_status) {
    case "paid":
      return { label: o.paid_on ? `Paid ${fmtDay(o.paid_on)}` : "Paid", tone: "emerald", title: `${sub}'s invoice${inv} was paid in Books.` };
    case "differs": {
      const words = differencesWithoutMoney(o.match_differences);
      return {
        label: `${sub} invoiced · differs`, tone: "amber",
        title: `${sub}'s invoice${inv} doesn't match the order${words.length ? `: ${words.join(" ")}` : "."} Shannon checks it in Books before it's paid.`,
      };
    }
    case "adjusted": {
      const who = firstName(o.adjusted_by_name) ?? "Shannon";
      const why = withoutMoney(o.adjusted_reason).trim().replace(/[.\s]+$/, "");
      return {
        label: `Adjusted by ${who}${why ? `: ${why.length > 60 ? `${why.slice(0, 57)}...` : why}` : ""}`, tone: "amber",
        title: `${who} changed the order in Books to what ${sub} billed${why ? `: ${why}` : ""}. Chad was told.`,
      };
    }
    case "billed":
      return { label: `${sub} invoiced · matches`, tone: "emerald", title: `${sub}'s invoice${inv} matches the order in Books.` };
    case "open":
      if (!o.confirmed_at && o.last_error && o.attempts >= 5) {
        return { label: "Order didn't reach Books", tone: "rose", title: `Sent ${o.attempts} times without Books taking it: ${o.last_error}` };
      }
      return o.confirmed_at
        ? { label: "Order in Books", tone: "sky", title: `Books has the order; it matches ${sub}'s invoice to it when the invoice comes in.` }
        : { label: "Order sent to Books", tone: "slate", title: "Waiting on Books to confirm it." };
    default:
      return null;
  }
}

export const TRADE_ORDER_TONE_CLASSES: Record<TradeOrderTone, string> = {
  slate: "bg-slate-100 text-slate-700",
  sky: "bg-sky-100 text-sky-800",
  emerald: "bg-emerald-100 text-emerald-800",
  amber: "bg-amber-100 text-amber-800",
  rose: "bg-rose-100 text-rose-800",
};
